<template>
  <div class="content-area">
    <div v-if="showToolbar || categoryTitle || $slots.headerTranslit" class="header-section">

      <!-- Default layout: ≥576px -->
      <ArticleHeaderDefault class="header-layout-default">
        <template #title>
          <NewsListTitle
              v-if="categoryTitle"
              :title="categoryTitle"
              :category-id="categoryId"
          />
        </template>
        <template v-if="$slots.headerTranslit" #translit>
          <slot name="headerTranslit"></slot>
        </template>
        <template v-if="categoryTitle" #flags>
          <div class="flags-row" role="button" tabindex="0" @click="goToSettingsOrIntro" @keydown.enter="goToSettingsOrIntro">
            <img :src="nativeFlagUrl" :alt="routeParams.nativeLanguage + ' flag'" class="flag-image">
            <span class="flag-arrow">→</span>
            <img :src="learnedFlagUrl" :alt="routeParams.learnedLanguage + ' flag'" class="flag-image">
          </div>
        </template>
        <template v-if="showToolbar" #stats>
          <slot name="toolbar">
            <WordStatsWidget
                @collectionClick="$emit('collectionClick')"
                @repeatClick="$emit('repeatClick')"
                @dailyClick="$emit('dailyClick')"/>
          </slot>
        </template>
      </ArticleHeaderDefault>

      <!-- Compact layout: <576px -->
      <ArticleHeaderCompact class="header-layout-compact">
        <template #title>
          <NewsListTitle
              v-if="categoryTitle"
              :title="categoryTitle"
              :category-id="categoryId"
          />
        </template>
        <template v-if="$slots.headerTranslit" #translit>
          <slot name="headerTranslit"></slot>
        </template>
        <template v-if="showToolbar" #stats>
          <slot name="toolbarCompact">
            <WordStatsWidget
                @collectionClick="$emit('collectionClick')"
                @repeatClick="$emit('repeatClick')"
                @dailyClick="$emit('dailyClick')"/>
          </slot>
        </template>
      </ArticleHeaderCompact>

    </div>
    <div class="content-slot">
      <slot></slot>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useRouter } from 'vue-router';
import WordStatsWidget from './WordStatsWidget.vue';
import NewsListTitle from './header/news-list-title.vue';
import ArticleHeaderDefault from './header/ArticleHeaderDefault.vue';
import ArticleHeaderCompact from './header/ArticleHeaderCompact.vue';
import { useCommonRouteParams } from '../../composables/useCommonRouteParams';
import { getFlagUrl } from '../utils/flag-utils';
import { getCurrentUserStore } from '../../store/services';

interface Props {
  categoryTitle?: string;
  categoryId?: string;
  showToolbar?: boolean;
}

withDefaults(defineProps<Props>(), {
  showToolbar: true
});

defineEmits<{
  (e: 'collectionClick'): void;
  (e: 'repeatClick'): void;
  (e: 'dailyClick'): void;
}>();

const routeParams = useCommonRouteParams();
const nativeFlagUrl = computed(() => getFlagUrl(routeParams.nativeLanguage));
const learnedFlagUrl = computed(() => getFlagUrl(routeParams.learnedLanguage));

const router = useRouter();
const userStore = getCurrentUserStore();
const goToSettingsOrIntro = async (): Promise<void> => {
  await userStore.initialize();
  const isAuthenticated = userStore.getCurrentUserId() != null;
  await router.push(isAuthenticated ? '/settings' : '/intro');
};
</script>

<style scoped>
.content-area {
  background: #ffffff;
  color: #1f2937;
  min-height: 100vh;
  width: 100%;
  display: flex;
  flex-direction: column;
  border-radius: 10px;
  padding-left: 20px;
  padding-right: 20px;
}

.header-section {
  padding: 20px 40px 0 40px;
}

.header-layout-default {
  display: flex;
}

.header-layout-compact {
  display: none;
}

.flags-row {
  display: none;
  gap: 8px;
  cursor: pointer;
  margin-top: -10px;
}

.flag-image {
  width: 26px;
  height: 19px;
  object-fit: cover;
  border-radius: 2px;
  border: 1px solid #d1d5db;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.flag-arrow {
  font-size: 15px;
  color: #6B7280;
}

.content-slot {
  flex: 1;
  padding: 0px 40px;
}

@media (max-width: 800px) {
  .content-area {
    padding-left: 12px;
    padding-right: 12px;
  }

  .header-section {
    padding: 16px 12px 0 12px;
  }

  .content-slot {
    padding: 12px;
  }

  .flag-image {
    width: 22px;
    height: 17px;
  }

  .flag-arrow {
    font-size: 13px;
  }
}

@media (max-width: 576px) {
  .content-area {
    padding-left: 8px;
    padding-right: 8px;
  }

  .header-section {
    padding: 10px 8px 12px 8px;
  }

  .content-slot {
    padding: 8px;
  }

  .flag-image {
    width: 19px;
    height: 14px;
  }

  .flag-arrow {
    font-size: 11px;
  }

  .header-layout-default {
    display: none;
  }

  .header-layout-compact {
    display: flex;
  }

}
</style>
